import React, { useState } from 'react';
import { ExternalLink, TrendingUp, Github, Eye, ImageIcon, X, Play, Info, Star, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const Projects = ({ data }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0, scale: 0.9 },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };
  return (
    <section id="projects" className="py-24 px-4 sm:px-6 lg:px-8" ref={ref}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-20">
          <h2 className="text-5xl sm:text-6xl font-bold text-white mb-4 tracking-tight">
            Recent Projects
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-400 mx-auto mb-6" />
          <p className="text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed">
            A selection of projects showcasing my expertise in building scalable applications and delivering measurable business impact
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {data.filter(project => project.featured).map((project, index) => (
            <div
              key={project.id}
              className="group"
            >
              <Card className="bg-gradient-to-br from-gray-900/60 to-gray-900/40 border border-gray-800/50 hover:border-emerald-500/30 transition-all duration-300 relative overflow-hidden h-full flex flex-col hover:shadow-2xl hover:shadow-emerald-500/10 group/card">
                
                {/* Project Image */}
                <div className="h-56 bg-gray-800/50 relative overflow-hidden group-hover/card:bg-gray-800/70 transition-colors duration-300">
                  {project.screenshot ? (
                    <img 
                      src={project.screenshot} 
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  
                  <div className="w-full h-full flex items-center justify-center text-gray-500" style={project.screenshot ? {display: 'none'} : {}}>
                    <ImageIcon className="h-16 w-16 opacity-50" />
                  </div>
                  
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                    <div className="flex gap-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedProject(project);
                        }}
                        className="px-5 py-2.5 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-sm"
                      >
                        Details
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(project.productUrl, '_blank');
                        }}
                        className="px-5 py-2.5 bg-emerald-500 text-white rounded-lg font-semibold hover:bg-emerald-600 transition-colors flex items-center gap-2 text-sm"
                      >
                        Visit
                        <ExternalLink className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                <CardHeader className="pb-3 pt-6 px-6">
                  <h3 className="text-xl font-bold text-white mb-3 leading-tight group-hover/card:text-emerald-400 transition-colors duration-300">
                    {project.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed line-clamp-2 group-hover/card:text-gray-300 transition-colors duration-300">
                    {project.description}
                  </p>
                </CardHeader>
                
                <CardContent className="px-6 pb-6 flex-1 flex flex-col justify-between">
                  <div className="mb-5">
                    <div className="flex items-center gap-2 text-emerald-400 mb-2">
                      <TrendingUp className="h-3.5 w-3.5" />
                      <span className="text-xs font-semibold uppercase tracking-wider">Impact</span>
                    </div>
                    <p className="text-sm text-gray-300 leading-relaxed">
                      {project.impact}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2 pt-5 border-t border-gray-800/50 group-hover/card:border-emerald-500/20 transition-colors duration-300">
                    {project.tech.map((tech, techIndex) => (
                      <Badge key={techIndex} className="bg-gray-800/60 text-gray-300 border border-gray-700/50 hover:border-emerald-500/50 hover:bg-emerald-900/20 hover:text-emerald-300 text-xs px-3 py-1 font-medium transition-all duration-200">
                        {tech}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>

      {/* Project Modal */}
        <AnimatePresence>
          {selectedProject && (
            <motion.div
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedProject(null)}
            >
              <motion.div
                className="bg-gray-900 border border-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
                initial={{ scale: 0.8, opacity: 0, y: 50 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.8, opacity: 0, y: 50 }}
                onClick={(e) => e.stopPropagation()}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                {/* Modal Header */}
                <div className="relative p-6 border-b border-gray-800">
                  <motion.button
                    className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                    onClick={() => setSelectedProject(null)}
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <X className="h-5 w-5" />
                  </motion.button>
                  
                  <motion.div
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.1 }}
                  >
                    <h2 className="text-2xl font-bold text-gray-100 mb-2 tracking-tight">
                      {selectedProject.title}
                    </h2>
                    <div className="flex items-center gap-4">
                      <Button
                        onClick={() => window.open(selectedProject.productUrl, '_blank')}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 text-sm"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View Project
                      </Button>
                    </div>
                  </motion.div>
                </div>
                
                {/* Modal Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                  <motion.div
                    className="space-y-6"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    {/* Project Image */}
                    {selectedProject.screenshot && (
                      <div className="rounded-lg overflow-hidden">
                        <img
                          src={selectedProject.screenshot}
                          alt={selectedProject.title}
                          className="w-full h-64 object-cover"
                        />
                      </div>
                    )}
                    
                    {/* Description */}
                    <div>
                      <h3 className="text-lg font-semibold text-emerald-400 mb-3 tracking-tight">Overview</h3>
                      <p className="text-gray-300 text-base leading-relaxed">
                        {selectedProject.description}
                      </p>
                    </div>
                    
                    {/* Impact */}
                    <div className="bg-gray-800/30 p-4 rounded-lg border border-gray-700">
                      <div className="flex items-center space-x-2 text-emerald-400 mb-2">
                        <Zap className="h-5 w-5" />
                        <span className="font-semibold text-base">Key Impact</span>
                      </div>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        {selectedProject.impact}
                      </p>
                    </div>
                    
                    {/* Technologies */}
                    <div>
                      <h3 className="text-lg font-semibold text-emerald-400 mb-3 tracking-tight">Technologies Used</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedProject.tech.map((tech, index) => (
                          <motion.div
                            key={index}
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: index * 0.05 }}
                            whileHover={{ scale: 1.1 }}
                          >
                            <Badge className="bg-emerald-900/30 text-emerald-300 border border-emerald-700/50 hover:bg-emerald-800/40 transition-colors">
                              {tech}
                            </Badge>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
    </section>
  );
};

export default Projects;